import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Card, Col, Layout, notification, Row} from 'tea-component';
import {connect, history, Location, shallowEqual, useDispatch, useParams, useSelector} from 'umi';
import {isEmpty, omit} from 'lodash-es';
import {useDebounceEffect, useSessionStorageState, useUnmount} from 'ahooks';
import classNames from 'classnames';
import {getLocalText, useDblIntl} from '@/pages/datalab/utils/utils';
import {buriedPoint} from '@/pages/home/utils/buriedPoint';
import {storage} from "@/utils/utils";
import {getLocaleState} from "@home/utils/state";
import {
  CHART_EXPLORE_FILTERS, 
  DATALAB_ANALYSIS_ERROR_CODE,
  CHART_EXPLORE_USER_FILTER,
  DATALAB_EDITOR,
  SYSTEM_NAME
} from '@dbl/core/global';
import { ChartType } from '@dbl/core/chart';
import { ChartCalculateMode } from '@dbl/core/chartEditor';
import {checkAllConditions, checkRawColumnModeConditions} from '../utils';
import {namespace} from './model';
import {useGetDataLabGuideDataList} from '../../Home/hoooks';
import GuideOperation from './components/GuideOperation/index';
import DataSource from './components/DataSource';
import Search from '@/pages/datalab/pages/Multidimensional/Analysis/components/Search';
import {CustomPrompt} from '@/pages/datalab/components/CustomPrompt';
import SwitchChartType from '@/pages/datalab/pages/Multidimensional/Analysis/components/SwitchChartType';
import ChartSettingsV2 from '@/pages/datalab/pages/Multidimensional/Analysis/components/ChartSettingsV2';
import { ChartSettingsProvider } from '@/pages/datalab/pages/Multidimensional/Analysis/components/ChartSettingsV2/context';
import {
  restoreChartSettingsFromCache,
  resetChartSettingsSession,
  syncChartSettingsBaseFromModel,
} from '@/pages/datalab/pages/Multidimensional/Analysis/components/ChartSettingsV2/utils/chartSettingsBridge';
import type { TChartSettingsSessionCache } from '@/pages/datalab/pages/Multidimensional/Analysis/components/ChartSettingsV2/utils/chartSettingsSession';
import {SaveModel} from "@/pages/datalab/pages/Multidimensional/Analysis/components/SaveModel";
import {GlobalPositioningGuidance} from '../../Home/GettingStart';
import FullLoading from '@/pages/home/components/FullLoading';
import './index.scss';
import {getTokenFailureKey} from '@dbl/services/common';

const { Content } = Layout;

/** 左侧菜单进入拖拉拽「新建空白页」：无 taskId / tableId / 号码包 / 旧 editMsg 等深链参数 */
function isMultiAnalysisMenuBlankEntry(
  pathname: string,
  query: Record<string, unknown> = {},
): boolean {
  if (!pathname.includes('Analysis/MultiAnalysis')) {
    return false;
  }
  return (
    !query.taskId &&
    !query.tableId &&
    !query.package_uuid &&
    !query.editMsg
  );
}

const PAGE_CONFIG = {
  add: {
    from: 'add',
    isHideLeftMenu: true,
    system: 'datalab',
    chartTitleCn: '未命名图表', // 固定的文本 中文
    chartTitleEn: 'Unnamed Chart', // 固定的文本 英文
    buriedPointConfig: {
      pageId: '600201',
      pageName: '拖拉拽分析', //
      extInfo: `{"pageName": "Drag & Drop", "pageNameCn": "拖拉拽分析", "id": ""}`,
      // uid: gameCode, // Datalab系统的gameCode
      typeId: "datalab_pc_v2", // Datalab系统
      menuId: '0',
      pagePath: '/Analysis/MultiAnalysis'
    },
    saveChart: {
      type: 'add',
      isShow: true,
      buttonTextId: 'save',
      titleId: 'save_chart'
    },
  },
  edit: {
    from: 'edit',
    isHideLeftMenu: true,
    system: 'datalab',
    buriedPointConfig: {
      pageId: '600202',
      // pageName: '', //
      // extInfo: `{"pageName": "${editMsg.name}", "pageNameCn": "", "id": "${taskId}"}`,
      // uid: gameCode, // Datalab系统
      typeId: "datalab_pc_v2", // Datalab系统
      menuId: '0',
      pagePath: '/Analysis/MultiAnalysis',
    },
    saveChart: {
      type: 'edit',
      isShow: true,
      buttonTextId: 'save',
      titleId: 'save_chart'
    },
    // 左上角返回按钮的 方法
    goBackButton: () => history.goBack(), // 弹出Leave弹窗
    leaveConfirm: (location: Location) => history.replace(location),
  },
  datalabExplore: {
    from: 'datalabExplore',
    system: 'datalab',
    // 左上角的图表名称
    chartTitle: '', // TODO 路径取值, 保存后要根据用户保存的数据展示
    // TODO save保存后, 要变成 from 'edit' 所以要根据这个对埋点数据进行变更
    // 埋点配置
    buriedPointConfig: {
      pageId: '601701', // TODO Datalab系统????
      // uid: gameCode, // Datalab系统
      typeId: "datalab_pc_v2",// Datalab系统
      menuId: '0',
      pageName: (name: string) => `${name}(Explore)`,
      pagePath: '/Analysis/MultiAnalysis'
    },
    // 左上角返回按钮的 方法
    goBackButton: () => history.goBack(), // 弹出Leave弹窗
    leaveConfirm: (location: Location) => history.replace(location),
    // 保存按钮
    //   文本, 内容, 其他
    saveChart: {
      type: 'add',
      isShow: true,
      buttonTextId: 'save_as',
      titleId: 'save_as_chart',
      saveSuccessCallback: (params: any) => {
        const {isEn, gameCode, ...query} = params;
        notification.success({
          description: (<div style={{display: 'flex', alignContent: 'center'}}>
            {isEn ? (<span>Save successful. To view, please go to&nbsp;</span>) : '保存成功，如需查看请前往'}
            <Button style={{lineHeight: '17px'}} type={'link'} onClick={() => history.push(`/datalab/${gameCode}/WorkBench/MyAnalysis`)}>{isEn ? '"Chart"' : '"图表中心"'}</Button>
          </div>),
        });
        history.replace({
          pathname: `/${SYSTEM_NAME}/${gameCode}/Analysis/MultiAnalysis`,
          query: {
            from: 'edit',
            ...query
            // taskId: resp?.data?.task_id,
            // name: nameKey,
            // nameCn: nameCnKey,
          }
        });
      }
    },
    // 号码包关联
    numberPackage: {
      disabled: true,
    },
    // 数据源
    dataSource: {
      disabled: true,
    },

    // 左侧菜单栏是否收起 ?? (经分那边没有左侧菜单栏)
    isHideLeftMenu: true,
    
    // 继承的筛选器保存在session的key
    inheritFiltersSessionKey: CHART_EXPLORE_FILTERS,
    
    // 继承的User filter保存在session的key
    inheritUserFilterSessionKey: CHART_EXPLORE_USER_FILTER,
    
    // 返回首页
    goHome: (gameCode: string) => {
      history.replace(`/datalab/${gameCode}/Home`);
    }
  },
  dashboardExplore: { // 只读, 不能保存的图表探索页面  经分系统
    from: 'dashboardExplore',
    isHideLeftMenu: true,
    // system: 'dashboard',
    // buriedPointConfig: {
    //   pageId: '205801', // TODO 经分系统????
    //   // uid: gameCode, // 经分系统的 TODO: 从当前路径中获取gameCode
    //   typeId: "dashboard_pc_v2", // 经分系统
    //   // menuId: '0', // 使用经分承接页面的menuId
    //   pageName: (name: string) => `${name}(Explore)`,
    //   pagePath: '/datalab/chart/explore'
    // },

    // 左上角返回按钮的 方法
    goBackButton: () => {
      history.goBack();
    }, // 弹出Leave弹窗
    leaveConfirm: (location: Location) => {
      getLocaleState('datalabMessageBus')?.notifyGoBack();
      history.replace(location);
    },
    // 号码包关联
    numberPackage: {
      disabled: true,
    },
    // 数据源
    dataSource: {
      disabled: true,
    },
    // 继承的筛选器保存在session的key
    inheritFiltersSessionKey: CHART_EXPLORE_FILTERS,
    
    // 继承的User filter保存在session的key
    inheritUserFilterSessionKey: CHART_EXPLORE_USER_FILTER,
  
    // 返回首页
    // goHome: (gameCode: string) => {
    //   history.replace(`/dashboard/game/${gameCode}`);
    // }
  },
  
};

export default connect((state: any, ownProps) => {
  const {
    loading: { effects }
  } = state;
  const {
    hashEqualForSave,
    dataTables,
    columnFilters = [],
    columnCompares = [],
    columnMetrics = [],
    columnDimensions = [],
    columnRawColumns = [],
    columnX=[],
    columnShowDimensions=[],
    mode,
    saveObj,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    task_id,
    hashEqualForAnalysis,
    chartType,
    gameCode,
    analysisRunning,
    table,
    ...restState
  } = state[namespace];
  return {
    dataTables,
    columnFilters,
    columnCompares,
    columnMetrics,
    columnDimensions,
    columnRawColumns,
    columnX,
    columnShowDimensions,
    mode,
    loading: effects[`${namespace}/getTableList`],
    hashEqualForSave,
    saveObj,
    task_id,
    getTableInfoLoading: state.loading.effects[`${namespace}/getTableInfo`],
    analysisLoading: state.loading.effects[`${namespace}/getDataAnlysis`],
    analysisRunning,
    table,
    hashEqualForAnalysis,
    chartType,
    gameCode: gameCode || ownProps.gameCode,
    ...restState
  };
})(Analysis);

function Analysis(props: any) {
  // const { gameCode } = useSelector(state => ({ gameCode: state.datalab.gameCode })); // TODO: 需要注意从经分跳转过来的话这个gameCode!!!!!!!
  const dispatch = useDispatch();
  const {
    dataTables,
    columnFilters,
    columnCompares,
    columnDimensions,
    columnX,
    columnShowDimensions,
    columnMetrics,
    columnRawColumns,
    mode,
    loading,
    hashEqualForSave,
    chartType,
    // saveObj,
    hashEqualForAnalysis,
    gameCode: reportGameCode, // 承接页面传递过来的gameCode
    analysisLoading,
    analysisRunning,
    table,
  } = props;
  const isLoading = analysisRunning || analysisLoading;
  const isTableState = isLoading || isEmpty(table) || (table && (table?.actualCount === 0));
 
  const {gamecode}: any = useParams()||{};
  const gameCode = gamecode || reportGameCode;
  const {allPrivileges} = useSelector((state: any) => ({
    allPrivileges: state.privilegesConfig.allPrivileges,
  }), shallowEqual);
  const disabledChartPrivilege = allPrivileges?.includes(DATALAB_EDITOR.AddChartBtn);
  const taskId = props?.location?.query?.taskId || '';

  const tableStr = props?.location?.query?.tableId;

  const autoSelectTable = props?.location?.query?.autoSelect === '1';
  const tableId = tableStr ? tableStr.split(',') : [];
  const packageUuid = props?.location?.query?.package_uuid;

  const [left, setLeft] = useState(false);
  const [top, setTop] = useState(false);
  /** 递增后 remount ChartSettingsProvider，与 DVA resetState 对齐，清 QB 联动 pipeline 内部 ref */
  const [pageSessionKey, setPageSessionKey] = useState(0);
  /** ChartSettingsProvider session 同步写入；useTokenFailureSetSessionCache 合并进 datalabChartsCatch.chartSettings */
  const chartSettingsCacheRef = useRef<TChartSettingsSessionCache>();
  /** resetToBlankAddPage 100ms 内去重，避免 pageConfig effect 与 history.listen 重复重置 */
  const lastBlankResetAtRef = useRef(0);
  const { dblFormatMessage, commonFormatMessage, isEn, formatMessage } = useDblIntl('multi');
  const [showSaveModel, setShowSaveModel] = useState(false);
  const { loading: guideLoading, guideSubTab, isShowGuide } = useGetDataLabGuideDataList();
  const pageConfig = useMemo(() => {
    const {from='add', name, nameCn, menuId} = props?.location?.query?.editMsg ? JSON.parse(props?.location?.query?.editMsg) : props?.location?.query; // JSON.parse是为了兼容旧路径
    const defaultConfig = PAGE_CONFIG[from] ?? {};
    
    return {
      ...defaultConfig,
      // 左上角展示图表名称
      chartTitleCn: defaultConfig?.chartTitleCn || nameCn,
      chartTitleEn: defaultConfig?.chartTitleEn || name,
      buriedPointConfig: defaultConfig?.buriedPointConfig ? {
        ...defaultConfig?.buriedPointConfig,
        pageName: ( typeof defaultConfig?.buriedPointConfig?.pageName === 'function') ? defaultConfig.buriedPointConfig.pageName(name) : (defaultConfig?.buriedPointConfig?.pageName || name),
        uid: gameCode, // 为了兼容经分系统的图表探索下钻页面, 所以使用路径中的gamecode作为打点参数
        extInfo: defaultConfig?.buriedPointConfig?.extInfo || `{"pageName": "${name}", "pageNameCn": "${nameCn}", "id": "${taskId}"}`,
        menuId: menuId || defaultConfig?.buriedPointConfig?.menuId
      } : undefined
    };
  }, [props?.location?.query]);
  const paramsCatch = useMemo(
    () => ({
      ...omit(props, ['table', 'chartData', 'reqParams']),
      columnName: props?.table?.columnName,
    }),
    [props],
  );
  const {handleOnSuccessCache, userFailureValue, takeEffect} = useTokenFailureSetSessionCache(
    paramsCatch,
    chartSettingsCacheRef,
  );
  const userFailureValueRef = useRef(userFailureValue);
  userFailureValueRef.current = userFailureValue;

  /**
   * 登录失效恢复：DVA 写回后同步 ChartSettings Context。
   * 优先用 session 里的 chartSettings 全量快照（含 draft）；无快照时从 DVA chartsCfgSave 推导 base。
   */
  const restoreChartSettingsAfterDvaRestore = useCallback(
    (cachedSnapshot?: { chartSettings?: TChartSettingsSessionCache }) => {
      const chartSettings =
        cachedSnapshot?.chartSettings ?? userFailureValue?.chartSettings;
      if (chartSettings) {
        restoreChartSettingsFromCache(chartSettings);
        return;
      }
      syncChartSettingsBaseFromModel(namespace);
    },
    [userFailureValue],
  );

  /**
   * 产品语义：再点「拖拉拽分析」菜单 = 新建空白页。
   * 同时重置 DVA model、ChartSettings Context，并 bump pageSessionKey 强制 Provider remount。
   * takeEffect 期间跳过，避免覆盖登录失效恢复中的快照。
   */
  const resetToBlankAddPage = useCallback(() => {
    const now = Date.now();
    if (now - lastBlankResetAtRef.current < 100) {
      return;
    }
    lastBlankResetAtRef.current = now;
    dispatch({
      type: `${namespace}/resetState`,
      payload: { gameCode },
    });
    resetChartSettingsSession();
    setPageSessionKey(key => key + 1);
  }, [dispatch]);

  const getInitialCatchChartData = (catchValue: any) => {
    // TODO: 排序数据，是根据columnName来处理，表格数据是获取接口，接口数据可能没有在对应columnName中，会缺失这一列，除非缓存表格数据，代价太大
    if (takeEffect) {
      const cachedSnapshot = userFailureValueRef.current;
      if (!isEmpty(catchValue)) {
        catchValue.table.columnName = cachedSnapshot?.columnName;
      }
      dispatch({
        type: `${namespace}/save`,
        payload: {
          ...cachedSnapshot,
          analysisRunning: false,
          calculateHashForSave: true,
          analysisReqId: '',
          ...catchValue,
        },
      });
      // DVA 写回后同步 ChartSettings（session chartSettings 或 chartsCfgSave）
      restoreChartSettingsAfterDvaRestore(cachedSnapshot);
    }
  };
  useEffect(() => {
    // 默认直接首次进入直接先清空
    dispatch({
      type: `${namespace}/save`,
      payload: {
        gameCode,
        system: pageConfig.system,
      }
    });
    
    // 图表探索页面 保存后, 不需要二次请求数据
    if (pageConfig.inheritFiltersSessionKey && !storage.getSessionStorageItem(pageConfig.inheritFiltersSessionKey)) return;
    
    // 如果是从我的图表, 数据表管理, 号码包管理页面跳转到拖拉拽分析
    if (taskId || tableId.length > 0 || packageUuid) {
      props.dispatch({
        type: `${namespace}/getTableInfo`, // 先判断是否获取任务详情或数据表信息,
        payload: {
          taskId,
          packageUuids: packageUuid ? [packageUuid] : [],
          gameCode,
          tableIds: tableId,
          autoSelectTable,
          system: pageConfig.system,
        },
        callback: (taskRsp: any, packageUUid: number[]) => {
          if (taskRsp) {
            if (!taskRsp.edit && !pageConfig.inheritFiltersSessionKey) {
              // 如果用户对这个图表没有编辑权限, 且不是探索页面时, 将跳转到首页 (将图表路径复制给其他人的时候)
              history.replace(`/${SYSTEM_NAME}/${gameCode}/Home`);
              return;
            }
            // 从 我的图表 页面跳转过来, 且已获取到任务详情信息
            dispatch({
              type: `${namespace}/getTaskDetail`, // 将任务详情信息转换为前端格式
              payload: {
                response: taskRsp,
                task_id: taskId,
                inheritFilters: storage.getSessionStorageItem(pageConfig.inheritFiltersSessionKey),
                inheritUserFilter: storage.getSessionStorageItem(pageConfig.inheritUserFilterSessionKey)
              },
              callback: (chartConfig) => {
                syncChartSettingsBaseFromModel(namespace);

                const { chart_type: chartType } = taskRsp;
                props.dispatch({
                  type: `${namespace}/getDataAnlysis`,
                  payload: {
                    isEn,
                    gameCode,
                    customizeSetting: omit(chartConfig?.[chartType], [
                      'customSortKey',
                    ])?.customizeSetting,
                    // TODO: isSetWarnInfo ????
                    // isSetWarnInfo: pageConfig.from === 'dashboardExplore' && !newFlag  && chartType === ChartType.Line,
                  },
                  callback: (payload: {errorMsg: string;}) => {
                    if (payload?.errorMsg && payload?.errorCode !== 40005) {
                      notification.error({
                        className: 'dbl-analysis-msg',
                        description: DATALAB_ANALYSIS_ERROR_CODE?.includes(payload?.errorCode) ? formatMessage({id: `dbl_error_${payload?.errorCode || '1'}`}, {name: payload?.errorData, msg: payload?.errorMsg}) : payload?.errorMsg,
                        duration: 3000
                      });
                    }
                    dispatch({
                      type: `${namespace}/save`,
                      payload: {
                        calculateHashForSave: true,
                        analysisRunning: false,
                        analysisReqId: '',
                        dragState: {
                          currentDrag: '',
                          errorDrag: {},
                        },
                        ...(payload?.errorMsg
                          ? {
                              errorMsg: payload.errorMsg,
                              errorCode: payload.errorCode,
                              errorData: payload.errorData,
                            }
                          : {}),
                      },
                    });
                    getInitialCatchChartData(payload);
                    handleOnSuccessCache?.();
                  }
                });
              },
            });
          }
          if (tableId.length > 0) {
            // 从 数据表管理页面跳转过来, 将带过来的数据表设置为已选中的数据源
            dispatch({
              type: `${namespace}/save`,
              payload: { task_id: '', saveObj: {}, autoSelectTable }
            });
          }
          if (packageUUid.length > 0) {
            // 从 号码包管理页面跳转过来,获取号码包详情
            dispatch({
              type: `${namespace}/setUserPackages`,
              payload: { gameCode, packageUuids: packageUUid },
              callback: () => {
                dispatch({
                  type: `${namespace}/save`,
                  payload: { calculateHashForSave: true }
                });
              }
            });
          }
        }
      });
    } else {
      if(takeEffect){
        // 优先展示左侧
        getInitialCatchChartData({});
        const { hadAnalysed, columnFilters, columnMetrics, columnRawColumns } =
   userFailureValue || {};
        // 根据右上角过滤条件存在，且点击过计算，出现表格/图表数据在决定是否需要呼起接口请求
        if (
          hadAnalysed &&
   (!isEmpty(columnFilters) ||
     !isEmpty(columnMetrics) ||
     !isEmpty(columnRawColumns))
        ) {
          dispatch({
            type: `${namespace}/getDataAnlysis`,
            payload: {
              isEn,
            },
            callback: (payload: { errorMsg: string; }) => {
              if (payload?.errorMsg && payload?.errorCode !== 40005) {
                notification.error({
                  className: 'dbl-analysis-msg',
                  description: DATALAB_ANALYSIS_ERROR_CODE?.includes(payload?.errorCode) ? formatMessage({id: `dbl_error_${payload?.errorCode || '1'}`}, {name: payload?.errorData, msg: payload?.errorMsg}) : payload?.errorMsg,
                  duration: 3000
                });
              }
              getInitialCatchChartData(payload);
            },
          });
        }
      }
      handleOnSuccessCache?.();
    }
    
    return () => {
      props.dispatch({
        type: `${namespace}/resetState`
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // 埋点逻辑
  useEffect(() => {
    // 图表探索页面粘贴复制路径给其他人, 点击后 Datalab跳转到首页, 经分跳转到第一个菜单
    // pageConfig.inheritFiltersSessionKey 有值代表 有需要继承的筛选器
    // 获取不到继承的筛选器, Datalab跳转到首页, 经分跳转到第一个菜单
    if (pageConfig.inheritFiltersSessionKey && !storage.getSessionStorageItem(pageConfig.inheritFiltersSessionKey)) {
      pageConfig?.goHome?.(gameCode);
      return;
    }
    
    // 在拖拉拽分析页面, 点击菜单进入新建（from=add）需重置 DVA + ChartSettings
    // 覆盖：带 query 的编辑页去掉 query、首次从菜单进入等 pageConfig 变化场景
    if (!takeEffect && pageConfig.from === 'add') {
      resetToBlankAddPage();
    }
    
    // 图表探索 另存为之后 保留在当前页面, 需要进行图表编辑的打点
    pageConfig.buriedPointConfig && buriedPoint(pageConfig.buriedPointConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    
    return () => {
      // 离开页面的时候, 删除本地存储的继承的筛选器, 这样可以兼容刷新页面的情况
      // TODO 需要兼容经分的情况
      storage.removeSessionStorageItem(pageConfig.inheritFiltersSessionKey);
      storage.removeSessionStorageItem(pageConfig.inheritUserFilterSessionKey);
    };

  }, [pageConfig, resetToBlankAddPage]);
  
  // 左侧菜单展开收起
  useEffect(() => {
    if (pageConfig.isHideLeftMenu) {
      
      dispatch({
        type: 'datalab/save',
        payload: {
          isExpendLeftMenu: false
        }
      });
      
      return () => {
        // 展开左侧目录
        dispatch({
          type: 'datalab/save',
          payload: {
            isExpendLeftMenu: true
          }
        });
      };
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 图表区高度自适应
  // 注意：这里不能在 ResizeObserver 回调里“同步”改写影响布局的样式（例如 minHeight），
  // 否则很容易触发浏览器的 ResizeObserver 保护机制：
  // `ResizeObserver loop completed with undelivered notifications`。
  // 处理策略：
  // - 将样式写入延迟到下一帧（rAF），把多次 resize 合并为一次 DOM 写操作；
  // - 只有值发生变化时才写入，减少不必要的布局抖动；
  // - 首帧 DOM 可能未挂载，observe 前必须判空，且延迟一帧绑定避免 observe(null) 抛错。
  useEffect(() => {
    let rafId: number | null = null;
    let lastMinHeight = '';
    let pendingHeight: number | null = null;

    const applyMinHeight = (): void => {
      rafId = null;
      if (pendingHeight === null) return;

      const $ele = document.querySelector(
        '.dbl-chat-container .tea-layout__content-body-inner',
      ) as HTMLDivElement | null;
      if (!$ele) return;

      const nextMinHeight = `${pendingHeight + 634}px`;
      if (nextMinHeight !== lastMinHeight) {
        $ele.style.minHeight = nextMinHeight;
        lastMinHeight = nextMinHeight;
      }
    };

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        pendingHeight = entry.contentRect.height;
      }
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(applyMinHeight);
    });

    const startObserve = (): (() => void) | undefined => {
      const element = document.querySelector(
        '.dbl-chat-container .dtb-dl-ans-search-container',
      ) as Element | null;
      if (!element) return;

      ro.observe(element);
      return () => {
        ro.unobserve(element);
      };
    };

    // 首帧可能取不到节点，延迟一帧再绑定，避免直接 observe(null)
    let stopObserve: (() => void) | undefined;
    const bindRafId = window.requestAnimationFrame(() => {
      stopObserve = startObserve();
    });

    return () => {
      window.cancelAnimationFrame(bindRafId);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      stopObserve?.();
      ro.disconnect();
    };
  }, []);
  
  const isRawColumnMode = (chartType === ChartType.Table) && (mode === ChartCalculateMode.RawColumn);
  const { isCheckSuccess } = isRawColumnMode ? checkRawColumnModeConditions(chartType, {columnFilters, columnRawColumns}, isEn) : checkAllConditions(chartType, {columnFilters, columnCompares, columnMetrics, columnDimensions, columnX, columnShowDimensions}, isEn);
  
  const disableButton =
		JSON.stringify(dataTables) === '[]' ||
		loading || (!isCheckSuccess) || !hashEqualForAnalysis;
  
  const title = getLocalText(isEn, pageConfig.chartTitleCn, pageConfig.chartTitleEn);
  const isAddOrEdit = ['edit', 'add'].includes(pageConfig?.from);
  
  return (
    <Content className='dbl-multi-analysis-content'>
      { (isShowGuide && (guideLoading || isEmpty(dataTables))) && guideSubTab && <FullLoading />  || <GlobalPositioningGuidance/> }
      <Content.Header
        className='dbl-header'
        title={title}
        showBackButton={!!pageConfig.goBackButton}
        onBackButtonClick={pageConfig.goBackButton}
        operation={
          (pageConfig.saveChart && disabledChartPrivilege) ?
            (<Button type='primary' className='item-btn' disabled={disableButton} onClick={() => setShowSaveModel(true)}>
              {commonFormatMessage(pageConfig.saveChart.buttonTextId)}
            </Button>) : undefined
        }
      />
      <Content.Body full className={classNames('datalabe-analysis dbl-chat-container', { 'dbl-chat-table-container': ChartType.Table === chartType && !isTableState })}>
        {/*
          key={pageSessionKey}：菜单新建 / resetToBlankAddPage 时 remount，避免 Context 与 DVA 双源不一致。
          ChartSettingsSessionSync：debounce 写入 chartSettingsCacheRef，供 session 缓存与 token 恢复。
          注：离开页拦截（CustomPrompt）仅看 DVA hashEqualForSave，不含 Context hasDraft。
        */}
        <ChartSettingsProvider key={pageSessionKey}>
        <Row style={{ height: 'inherit' }}>
          <Col span={left ? '0' : '6'} className={left ? 'collapse-none' : 'collapse-left collapse-fixed'}>
            <Card className='analysis-number' style={{ height: 'inherit' }}>
              <Card.Body style={{ height: 'inherit' }}>
                <DataSource pageConfig={pageConfig}/>
              </Card.Body>
            </Card>
            <div
              className='expand-collapse-left'
              onClick={() => {
                setLeft(true);
              }}
            >
              <div className='expand-collapse-init' />
            </div>
          </Col>
          <Col span={left ? '0' : '6'} className={left ? 'collapse-none' : 'collapse-left'} style={{ height: 'inherit' }}>
          </Col>
          <Col span={left ? 24 : '18'} className='collapse-right' style={{ height: 'inherit', position: 'relative' }}>
            <SwitchChartType isEn={isEn} gameCode={gameCode} />
            <Search gameCode={gameCode} />
            {/* {top?1:2} */}
            <GuideOperation
              isShow={top}
              closeBtn={() => {
                setTop(false);
              }}
            />
            {left ? (
              <div
                className='expand-collapse-right'
                onClick={() => {
                  setLeft(false);
                }}
              >
                <div className='expand-collapse-init' />
              </div>
            ) : (
              ''
            )}
          </Col>
          <ChartSettingsV2 />
        </Row>
        {
          pageConfig.saveChart ? (<SaveModel
            showSaveModel={showSaveModel}
            setShowSaveModel={setShowSaveModel}
            namespace={namespace}
            dispatch={props.dispatch}
            data={pageConfig.saveChart.type === 'edit' ? {taskId: props.task_id, ...props.saveObj, sourceDashboardId: props?.location?.query.dashboardId} : undefined}
            title={dblFormatMessage(pageConfig.saveChart.titleId)}
            onSaveSuccess={pageConfig.saveChart.saveSuccessCallback}
          />) : ''
        }
        </ChartSettingsProvider>
      </Content.Body>
      {/* 没有保存离开页面弹出框 */}
      <CustomPrompt
        title={dblFormatMessage(isAddOrEdit ? 'leave_info' : 'explore_leave_info')}
        message={commonFormatMessage('leave_message', {name: isAddOrEdit?  '' : dblFormatMessage('exploration')})}
        isPrompt={!showSaveModel && (!hashEqualForSave || !isAddOrEdit)}
        onConfirm={pageConfig.leaveConfirm}
      />
      
    </Content>
  );
}

// TODO: 待迁移
/**
 * 页面级 session 缓存（datalabChartsCatch）：DVA 查询态 + ChartSettings Context 快照。
 * @param effect - 来自 connect props 的 Analysis 状态（不含 table/chartData 等大对象）
 * @param chartSettingsCacheRef - Provider 内 ChartSettingsSessionSync 维护的 Context 快照
 */
export const useTokenFailureSetSessionCache = (
  effect: any,
  chartSettingsCacheRef?: React.MutableRefObject<
    TChartSettingsSessionCache | undefined
  >,
) => {
  // TODO:
  // 1、数据回显,有缓存数据时，此时接口请求应该cancel
  // 2、hooks调用层级，影响到视图更新
  const [userFailureValue, setUserFailureValue] = useSessionStorageState(
    'datalabChartsCatch',
    {
      defaultValue: {},
    },
  );
  const takeEffect = window.sessionStorage.getItem('isUserFailure');
  useDebounceEffect(
    () => {
      if(!takeEffect) {
        setUserFailureValue((res) => ({
          // res 原始上一次
          // getTokenFailureKey 调用多个useTokenFailureSetSessionCache，参数共享一个key，调用方只关注需要监听的数据, 以路由为单位，缓存当前页面的数据，页面离开即销毁
          // effect 外部传入实时监听
          ...omit(
            {
              ...res,
              ...getTokenFailureKey(),
              ...effect,
              // base / draft / hasDraft 等，与 DVA 一并写入，供 isUserFailure 恢复
              chartSettings: chartSettingsCacheRef?.current,
            },
            [
            'children',
            'dispatch',
            'history',
            'route',
            'routes',
            'location',
            'match',
          ]),
        }));
      
      }
    },
    [effect, takeEffect, chartSettingsCacheRef],
    {
      wait: 1000,
    },
  );
  const handleOnSuccessCache = ()=>{
    window.sessionStorage.removeItem('isUserFailure');
  };
  // 利用完缓存，当次清空。不滞留
  useUnmount(() => {
    if(!takeEffect){
      setUserFailureValue({});
    }
  });
  return {
    handleOnSuccessCache,
    userFailureValue,
    takeEffect: !isEmpty(userFailureValue) && takeEffect,
  };
};


